import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../../hooks/useTranslation";
import { fetchMealByIdThunk, reorderMealComponentsThunk } from "../../store/slices/mealsReducer";
import { fetchPatientProfilesThunk } from "../../store/slices/patientProfilesReducer";
import { updateMeal, deleteMeal, acknowledgeMealProductSyncNotice } from "../../api/api_meals";
import { apiClient } from "../../api/api_base";
import MealTableRowComponent from "../../components/kostData/MealTableRowComponent";
import MealTableRowComponentNew from "../../components/kostData/MealTableRowComponentNew";
import MealSingleViewSummeryCard from "../../components/kostData/MealSingleViewSummeryCard";
import DishEnergyDistributionChart from "../../components/DishEnergyDistributionChart";
import CompositionPercentagesCard from "../../components/CompositionPercentagesCard";
import ParametersGroups from "../../components/ParametersGroups.jsx";
import { getDishAverages } from "../../utils/sustainabilityAverages";
import { calculateEnergyFromMacros } from "../../utils/energyCalculation";
import JourneyBreadcrumb from "../../components/navigation/JourneyBreadcrumb";
import SideDrawer from "../../components/navigation/SideDrawer";
import { CiViewList } from "react-icons/ci";
import { PiBowlFoodLight } from "react-icons/pi";
import { useAppAlertDialog } from "../../hooks/useAppAlertDialog";

const MealCreationPage = () => {
  const { t } = useTranslation();
  const { mealId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    currentMeal: item,
    status,
    error,
  } = useSelector((state) => state.meals);
  const { profiles: patients } = useSelector((state) => state.patientProfiles);
  const [adding, setAdding] = useState(false);
  const [filterCriteria] = useState({
    minSustainability: 0,
    maxSustainability: 100,
    origin: "",
    ingredientType: "",
  });
  const [draggingId, setDraggingId] = useState(null);
  const [components, setComponents] = useState([]);
  const [orderDirty, setOrderDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [showNutrientDrawer, setShowNutrientDrawer] = useState(false);
  const [showCompositionsDrawer, setShowCompositionsDrawer] = useState(false);
  const [showParametersModal, setShowParametersModal] = useState(false);
  const [showIngredientsExpanded, setShowIngredientsExpanded] = useState(false);
  const [copiedIngredients, setCopiedIngredients] = useState(false);
  const [description, setDescription] = useState("");
  const [savingDescription, setSavingDescription] = useState(false);
  const [mealType, setMealType] = useState("frukost");
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [mealName, setMealName] = useState("");
  const [savingMeal, setSavingMeal] = useState(false);
  const [isCreating] = useState(!mealId);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningPatient, setAssigningPatient] = useState(false);
  const [assignPatientId, setAssignPatientId] = useState(null);
  const [dismissedProductSyncNotice, setDismissedProductSyncNotice] = useState(false);
  const [closingProductSyncNotice, setClosingProductSyncNotice] = useState(false);
  const { showAlert, showConfirm, dialogNode } = useAppAlertDialog({
    cancelLabel: t("Avbryt"),
    confirmLabel: t("Fortsätt"),
    closeLabel: t("Stäng"),
  });
  
  useEffect(() => {
    if (showNutrientDrawer || showCompositionsDrawer) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
    return undefined;
  }, [showNutrientDrawer, showCompositionsDrawer]);


  // const getCountryCodeFromFlag = (url) => {
  //   if (!url || typeof url !== "string") return "";
  //   try {
  //     const last = url.split("/").pop() || "";
  //     const base = last.split(".")[0] || "";
  //     return (base || "").toUpperCase();
  //   } catch (e) {
  //     return "";
  //   }
  // };

  useEffect(() => {
    if (item?.components) {
      setComponents(item.components);
    } else {
      setComponents([]);
    }
  }, [item]);

  useEffect(() => {
    if (item) {
      setMealType(item.meal_type || "frukost");
      setDescription(item.description || "");
      setMealName(item.name || "");
      setSelectedPatientId(item.patient_profile_id || null);
    }
  }, [item]);

  useEffect(() => {
    setDismissedProductSyncNotice(false);
    setClosingProductSyncNotice(false);
  }, [mealId]);

  const handleCloseProductSyncNotice = async () => {
    if (!mealId || closingProductSyncNotice) return;
    setClosingProductSyncNotice(true);
    try {
      await acknowledgeMealProductSyncNotice(mealId);
      setDismissedProductSyncNotice(true);
    } catch (err) {
      console.error("Failed to acknowledge meal product sync notice:", err);
      setDismissedProductSyncNotice(true);
    } finally {
      setClosingProductSyncNotice(false);
    }
  };

  // Check if we're coming from patient flow (patientId in URL)
  const patientIdParam = searchParams.get('patientId');
  const isFromPatientFlow = !!patientIdParam;

  useEffect(() => {
    // If coming from patient flow, set patient ID and disable standalone meal creation
    // But first check if patient is active
    if (patientIdParam && !selectedPatientId) {
      const patient = patients.find(p => p.id.toString() === patientIdParam);
      if (patient) {
        if (patient.status === 'inactive') {
          showAlert(
            t("Kan inte fortsätta"),
            t('Denna patient är inaktiv. Aktivera patienten först innan du kan skapa måltider.') || 'Denna patient är inaktiv. Aktivera patienten först innan du kan skapa måltider.'
          );
          navigate('/kostdata/patients');
          return;
        }
        setSelectedPatientId(patientIdParam);
      }
    }
  }, [searchParams, selectedPatientId, patientIdParam, patients, navigate, t, showAlert]);

  useEffect(() => {
    // Load patients if needed (for patient selection, when coming from patient flow, or when assign modal might be used)
    if (patients.length === 0) {
      dispatch(fetchPatientProfilesThunk());
    }
  }, [dispatch, patients.length]);

  // Load patients when assign modal opens
  useEffect(() => {
    if (showAssignModal && patients.length === 0) {
      dispatch(fetchPatientProfilesThunk());
    }
  }, [showAssignModal, dispatch, patients.length]);

  const originalContentsDisplay = useMemo(() => {
    if (!components || components.length === 0) {
      return item?.original_contents || "";
    }

    const formatPercentage = (value) => {
      const num = parseFloat(value);
      if (!Number.isFinite(num)) return null;
      const rounded = parseFloat(num.toFixed(1));
      if (!Number.isFinite(rounded)) return null;
      return rounded % 1 === 0 ? `${rounded.toFixed(0)}%` : `${rounded.toFixed(1)}%`;
    };

    const stripPrefix = (text) => {
      if (!text || typeof text !== "string") return "";
      return text
        .replace(/^\s*Ingredienser\s*:\s*/iu, "")
        .replace(/\s+/g, " ")
        .trim();
    };

    // Sort components by percentage in descending order
    const sortedComponents = [...components].sort((a, b) => {
      const getPercentage = (comp) => {
        const pct = comp.percentage ?? comp.weight_in_percentage ?? comp.inserted_percentage;
        const num = parseFloat(pct);
        return Number.isFinite(num) ? num : 0;
      };
      return getPercentage(b) - getPercentage(a);
    });

    const parts = sortedComponents
      .map((component) => {
        const pctStr = formatPercentage(
          component.percentage ?? component.weight_in_percentage ?? component.inserted_percentage
        );

        const applyPercentage = (label) => {
          if (!label) return "";
          return pctStr ? `${label} (${pctStr})` : label;
        };

        const baseName =
          component.name ||
          component.product_name ||
          component.dish_name ||
          component.title ||
          component.label ||
          "";

        if (
          component.type === "ingredient" ||
          component.ingredient_id ||
          component.sourceType === "ingredient"
        ) {
          return applyPercentage(baseName);
        }

        if (
          component.type === "product" ||
          component.product_id ||
          component.sourceType === "product"
        ) {
          const productName = applyPercentage(baseName);
          const productContents = stripPrefix(
            component.original_contents ||
              component.product_original_contents ||
              component.contents ||
              ""
          );
          if (productContents) {
            return productName ? `${productName}: ${productContents}` : productContents;
          }
          return productName;
        }

        if (
          component.type === "recipe" ||
          component.child_dish_id ||
          component.sourceType === "recipe"
        ) {
          const childContents = stripPrefix(
            component.original_contents ||
              component.child_original_contents ||
              component.child_contents ||
              ""
          );
          if (childContents) {
            return childContents;
          }
          return applyPercentage(baseName);
        }

        return applyPercentage(baseName);
      })
      .filter((part) => part && part.length > 0);

    if (!parts.length) {
      return item?.original_contents || "";
    }

    return `Ingredienser: ${parts.join(", ")}`;
  }, [components, item?.original_contents]);

  const handleDragStart = (e, component) => {
    setDraggingId(component.id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, overComponent) => {
    e.preventDefault();
    if (!components || components.length === 0) return;
    if (draggingId == null || draggingId === overComponent.id) return;

    const current = [...components];
    const fromIndex = current.findIndex((c) => c.id === draggingId);
    const toIndex = current.findIndex((c) => c.id === overComponent.id);
    if (fromIndex === -1 || toIndex === -1) return;

    const [moved] = current.splice(fromIndex, 1);
    current.splice(toIndex, 0, moved);

    setComponents(current);
    setOrderDirty(true);
  };

  const handleSaveOrder = async () => {
    if (!orderDirty || savingOrder || !mealId) return;
    setSavingOrder(true);
    try {
      const componentIds = components.map((c) => c.id);
      await dispatch(reorderMealComponentsThunk({ mealId, componentIds })).unwrap();
      setOrderDirty(false);
      dispatch(fetchMealByIdThunk(mealId));
    } catch (err) {
      console.error("Failed to save order:", err);
    } finally {
      setSavingOrder(false);
    }
  };

  const handleSaveDescription = async () => {
    if (!mealId || savingDescription) return;
    setSavingDescription(true);
    try {
      await updateMeal(mealId, { description });
      dispatch(fetchMealByIdThunk(mealId));
    } catch (error) {
      console.error("Failed to update description:", error);
    } finally {
      setSavingDescription(false);
    }
  };

  const handleCreateMeal = async () => {
    if (!mealName.trim()) {
      showAlert(t("Valideringsfel"), t('Måltidsnamn är obligatoriskt'));
      return;
    }
    if (!mealType) {
      showAlert(t("Valideringsfel"), t('Måltidstyp är obligatorisk'));
      return;
    }

    setSavingMeal(true);
    try {
      const mealData = {
        name: mealName.trim(),
        meal_type: mealType,
        description: description || null,
        patient_profile_id: selectedPatientId || null,
        status: 'draft',
      };

      const response = await apiClient.post('/meals', mealData);
      const newMealId = response.data.data?.id || response.data.id;
      if (newMealId) {
        // If coming from patient flow, navigate back to patient profile
        if (isFromPatientFlow && selectedPatientId) {
          navigate(`/kostdata/patients/${selectedPatientId}`);
        } else {
          navigate(`/kostdata/meals/${newMealId}/edit`);
          dispatch(fetchMealByIdThunk(newMealId));
        }
      }
    } catch (error) {
      console.error('Error creating meal:', error);
      showAlert(
        t("Skapande misslyckades"),
        t('Kunde inte skapa måltid: ') + (error.response?.data?.error || error.message)
      );
    } finally {
      setSavingMeal(false);
    }
  };


  useEffect(() => {
    if (mealId) {
      dispatch(fetchMealByIdThunk(mealId));
    }
  }, [dispatch, mealId]);

  // Nutrients now come embedded with dish payload from backend

  const getAverageScore = (dish) => {
    const averages = getDishAverages(dish);
    const totalAverage =
      (averages.environment_average +
        averages.health_average +
        averages.social_average) /
      3;
    return Math.round(totalAverage);
  };

  const filteredComponents =
    (components || []).filter((component) => {
      const avgScore = getAverageScore({ ingredients: [component] });
      return (
        avgScore >= filterCriteria.minSustainability &&
        avgScore <= filterCriteria.maxSustainability &&
        (filterCriteria.origin === "" ||
          (component.origin || "")
            .toLowerCase()
            .includes(filterCriteria.origin.toLowerCase())) &&
        (filterCriteria.ingredientType === "" ||
          (component.name || "")
            .toLowerCase()
            .includes(filterCriteria.ingredientType.toLowerCase()))
      );
    }) || [];


  if (status === "loading") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-secondary-green border-t-transparent mx-auto"></div>
          <p className="mt-6 text-gray-600 text-lg font-medium">
            {t('Laddar recept...')}
          </p>
        </div>
      </div>
    );
  }

  if (error && !isCreating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <img
                src="/assets/icons/Radera.png"
                alt={t("Fel")}
                className="w-8 h-8 text-red-600"
              />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {t('Fel vid laddning av måltid')}
            </h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <Link
              to={isFromPatientFlow && selectedPatientId ? `/kostdata/patients/${selectedPatientId}` : "/"}
              className="bg-gradient-to-r from-secondary-green to-secondary-green/90 hover:from-secondary-green/90 hover:to-secondary-green text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {t('Tillbaka')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!item && !isCreating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <img
                src="/assets/icons/Innehall.png"
                alt={t("Hittades inte")}
                className="w-8 h-8 text-gray-400"
              />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {t('Måltid hittades inte')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t('Det begärda måltiden kunde inte hittas.')}
            </p>
            <Link
              to={isFromPatientFlow && selectedPatientId ? `/kostdata/patients/${selectedPatientId}` : "/"}
              className="bg-gradient-to-r from-secondary-green to-secondary-green/90 hover:from-secondary-green/90 hover:to-secondary-green text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {t('Tillbaka')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-4 tablet:px-6 laptop:px-8 desktop:px-8 py-8">
        <JourneyBreadcrumb
          items={[
            isFromPatientFlow
              ? { label: t('Patientprofiler') || 'Patientprofiler', to: '/kostdata/patients' }
              : { label: t('Dashboard') || 'Dashboard', to: '/' },
            isFromPatientFlow && selectedPatientId
              ? { label: patients.find(p => p.id === selectedPatientId)?.name || t('Patient'), to: `/kostdata/patients/${selectedPatientId}` }
              : item?.patient_profile_id
              ? { label: item.patient_profile?.name || t('Patient'), to: `/kostdata/patients/${item.patient_profile_id}` }
              : null,
            { label: isCreating ? t('Skapa måltid') : (item?.name || t('Måltid')) },
          ].filter(Boolean)}
          className="mb-6"
        />
        {item?.has_product_sync_notice && !dismissedProductSyncNotice && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start justify-between gap-3">
            <p className="text-sm text-amber-900 m-0">
              {t("Kopplade GTIN-produkter har uppdaterats. Måltiden visar nu senaste data.")}
            </p>
            <button
              type="button"
              onClick={handleCloseProductSyncNotice}
              disabled={closingProductSyncNotice}
              className="text-amber-800 hover:text-amber-950 text-sm font-semibold disabled:opacity-50"
            >
              {t("Stäng")}
            </button>
          </div>
        )}
        {/* Header */}
        <div className="mb-6 mobile:mb-8 tablet:mb-8 laptop:mb-8 desktop:mb-8">
          <div className="flex flex-col">
            <div>
              <div className="flex items-center justify-between mb-2 mobile:mb-3 tablet:mb-3 laptop:mb-3 desktop:mb-3 w-full">
                <Link
                  to={
                    isFromPatientFlow && selectedPatientId
                      ? `/kostdata/patients/${selectedPatientId}`
                      : item?.patient_profile_id
                      ? `/kostdata/patients/${item.patient_profile_id}`
                      : "/"
                  }
                  className="inline-flex items-center text-secondary-green hover:text-secondary-green/80 font-semibold transition-colors duration-200"
                >
                  <img
                    src="/assets/icons/arrow_right.png"
                    alt={t("Tillbaka")}
                    className="w-5 h-5 mr-2 transform rotate-180"
                  />
                  {t('Tillbaka')}
                </Link>
              </div>
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center space-x-4">
                  <h1 className={`${(isCreating ? mealName : item?.name)?.length > 20 ? 'text-xl mobile:text-2xl tablet:text-3xl laptop:text-3xl desktop:text-3xl' : 'text-2xl mobile:text-3xl tablet:text-4xl laptop:text-4xl desktop:text-4xl'} font-bold text-gray-900`}>
                    {isCreating ? mealName || t('Skapa ny måltid') : item?.name}
                  </h1>
                  {/* {item.country_flag && (
                    <div className="inline-flex items-center bg-white/90 backdrop-blur-sm border-2 border-gray-200 rounded-full shadow-none px-3 py-1.5">
                      <img
                        src={item.country_flag}
                        alt=""
                        className="h-5 w-auto rounded-sm mr-2"
                      />
                      <span className="text-sm font-bold text-gray-800 tracking-wide">
                        {getCountryCodeFromFlag(item.country_flag)}
                      </span>
                    </div>
                  )} */}
                </div>
                {/* Action Buttons */}
                {!isCreating && (
                <div className="flex items-center space-x-3 mobile:hidden">
                    {/* Assign/Unassign Patient Button */}
                    {!item?.patient_profile_id ? (
                  <button
                        onClick={() => setShowAssignModal(true)}
                        className="px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center bg-secondary-green hover:bg-secondary-green/90 text-white"
                      >
                        <img
                          src="/assets/icons/Plus.png"
                          alt={t("Tilldela")}
                          className="w-4 h-4 mr-2"
                        />
                        {t('Tilldela till patient')}
                      </button>
                    ) : (
                      <button
                        onClick={async () => {
                          const confirmed = await showConfirm(
                            t("Avmarkera patient"),
                            t('Vill du avmarkera denna måltid från patienten?')
                          );
                          if (!confirmed) return;
                          try {
                            await updateMeal(mealId, { patient_profile_id: null });
                            dispatch(fetchMealByIdThunk(mealId));
                          } catch (error) {
                            showAlert(t("Fel"), t('Kunde inte avmarkera patient: ') + error.message);
                          }
                        }}
                        className="px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center bg-white hover:bg-gray-50 text-gray-700 border border-gray-200"
                      >
                        <img
                          src="/assets/icons/Dolj.png"
                          alt={t("Avmarkera")}
                          className="w-4 h-4 mr-2"
                        />
                        {t('Avmarkera patient')}
                  </button>
                    )}
                  
                    {/* Delete Button */}
                    <button
                      onClick={() => setShowDeleteModal(true)}
                      className="px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center bg-red-600 hover:bg-red-700 text-white"
                    >
                      <img
                        src="/assets/icons/Radera_vit.png"
                        alt={t("Ta bort")}
                        className="w-4 h-4 mr-2"
                      />
                      {t('Ta bort måltid')}
                    </button>
                  </div>
                )}
              </div>
              <p className="text-sm mobile:text-base tablet:text-lg laptop:text-lg desktop:text-lg text-gray-600">
                {isCreating 
                  ? t('Skapa en ny måltid med ingredienser, produkter eller recept')
                  : t('Detaljerad information om måltiden och dess komponenter')}
              </p>
            </div>
          </div>
        </div>

        {/* Create Meal Form */}
        {isCreating && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-xl font-bold text-gray-900">{t('Skapa ny måltid')}</h3>
                  </div>
            <div className="px-6 py-6 space-y-6">
              {/* Meal Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Måltidsnamn')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={mealName}
                  onChange={(e) => setMealName(e.target.value)}
                  placeholder={t('Ange måltidsnamn...')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary-green focus:border-transparent"
                    />
                  </div>

              {/* Meal Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Måltidstyp')} <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: "frukost", label: t("Frukost") },
                    { value: "lunch", label: t("Lunch") },
                    { value: "middag", label: t("Middag") },
                    { value: "mellanmål", label: t("Mellanmål") },
                    { value: "småmål", label: t("Småmål") },
                    { value: "kvällsmål", label: t("Kvällsmål") },
                    { value: "desser", label: t("Desser") },
                    { value: "övrigt", label: t("Övrigt") },
                  ].map((type) => (
                    <label key={type.value} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        name="mealType"
                        checked={mealType === type.value}
                        onChange={() => setMealType(type.value)}
                        className="h-4 w-4 text-secondary-green focus:ring-secondary-green border-gray-300"
                      />
                      <span className="ml-3 text-sm text-gray-700">{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Patient Selection - Only show when NOT coming from patient flow */}
              {!isFromPatientFlow && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Patient (valfritt)')}
                  </label>
                  <select
                    value={selectedPatientId || ''}
                    onChange={(e) => {
                      const selectedId = e.target.value || null;
                      if (selectedId) {
                        const selectedPatient = patients.find(p => p.id.toString() === selectedId);
                        if (selectedPatient && selectedPatient.status === 'inactive') {
                          showAlert(
                            t("Kan inte välja patient"),
                            t('Kan inte välja inaktiv patient. Aktivera patienten först.') || 'Kan inte välja inaktiv patient. Aktivera patienten först.'
                          );
                          return;
                        }
                      }
                      setSelectedPatientId(selectedId);
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary-green focus:border-transparent"
                  >
                    <option value="">{t('Standalone måltid (ingen patient)')}</option>
                    {patients.filter(p => p.status === 'active').map((patient) => (
                      <option key={patient.id} value={patient.id}>
                        {patient.name}
                      </option>
                    ))}
                  </select>
                  {patients.filter(p => p.status === 'inactive').length > 0 && (
                    <p className="mt-1 text-xs text-gray-500">
                      {patients.filter(p => p.status === 'inactive').length} {t('inaktiva patienter döljs') || 'inaktiva patienter döljs'}
                    </p>
                  )}
                </div>
              )}
              
              {/* Show patient info when coming from patient flow */}
              {isFromPatientFlow && selectedPatientId && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-bold">
                        {patients.find(p => p.id === selectedPatientId)?.name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        {t('Måltid för patient:')} {patients.find(p => p.id === selectedPatientId)?.name || selectedPatientId}
                      </p>
                      <p className="text-xs text-blue-600">
                        {t('Denna måltid kommer att kopplas till patienten')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Create Button */}
              <div className="flex justify-end pt-4 border-t border-gray-100">
                <button
                  onClick={handleCreateMeal}
                  disabled={savingMeal || !mealName.trim() || !mealType}
                  className="bg-gradient-to-r from-secondary-green to-secondary-green/90 hover:from-secondary-green/90 hover:to-secondary-green disabled:from-gray-400 disabled:to-gray-400 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 flex items-center disabled:opacity-50"
                >
                  {savingMeal ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {t('Skapar...')}
                    </>
                  ) : (
                    <>
                      <img
                        src="/assets/icons/Check.png"
                        alt={t("Skapa")}
                        className="w-5 h-5 mr-2"
                      />
                      {t('Skapa måltid')}
                    </>
                  )}
                </button>
            </div>
          </div>
        </div>
        )}

        {/* Meal Overview Card */}
        {!isCreating && item && (
          <MealSingleViewSummeryCard
          item={item}
          onClick={() => {}}
          className="mb-8"
        />
        )}

        {/* Components Table */}
        {!isCreating && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-900">{t('Komponenter')}</h3>
              <div className="flex items-center space-x-4 mobile:hidden">
              <button
                  className="bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-4 rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] flex items-center"
                  onClick={() => setShowCompositionsDrawer(true)}
                >
                  <PiBowlFoodLight className="w-5 h-5 mr-2" />
                  {t("Hälsofaktorer")}
                </button>
                {/* Save order button moved to fixed bottom bar */}
                <button
                  className="bg-secondary hover:bg-gray-50 text-gray-700 font-semibold py-3 px-4 rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] flex items-center"
                  onClick={() => setShowNutrientDrawer(true)}
                >
                  <CiViewList className="w-5 h-5 mr-2" />
                  {t('Näringsvärde')}
                </button>
                <button
                  className="bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-4 rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] flex items-center"
                  onClick={() => setShowParametersModal(true)}
                >
                  <img
                    src="/assets/icons/svg/parameter.svg"
                    alt={t("Parametrar")}
                    className="w-6 h-6 mr-2"
                  />
                  {t('Parametrar')}
                </button>
                <button
                  onClick={() => setAdding(!adding)}
                  className={
                    adding
                      ? "bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-6 rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 flex items-center"
                      : "bg-gradient-to-r from-secondary-green to-secondary-green/90 hover:from-secondary-green/90 hover:to-secondary-green text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] flex items-center"
                  }
                >
                  {adding ? (
                    <>
                      <img
                        src="/assets/icons/Dolj.png"
                        alt={t("Avbryt")}
                        className="w-5 h-5 mr-2"
                      />
                      {t("Avbryt")}
                    </>
                  ) : (
                    <>
                      <img
                        src="/assets/icons/Plus.png"
                        alt={t("Lägg till")}
                        className="w-5 h-5 mr-2"
                      />
                      {t("Lägg till Komponent")}
                    </>
                  )}
                </button>
              </div>
            </div>
            {/* Mobile buttons under title */}
            <div className="hidden mobile:grid grid-cols-1 gap-3 mt-4">
            <button
                className="w-full justify-center bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-4 rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 flex items-center"
                onClick={() => setShowCompositionsDrawer(true)}
              >
                <PiBowlFoodLight className="w-5 h-5 mr-2" />
                {t("Hälsofaktorer")}
              </button>
              <button
                className="w-full justify-center bg-secondary hover:bg-gray-50 text-gray-700 font-semibold py-3 px-4 rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 flex items-center"
                onClick={() => setShowNutrientDrawer(true)}
              >
                <CiViewList className="w-5 h-5 mr-2" />
                {t('Näringsvärde')}
              </button>
              <button
                className="w-full justify-center bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-4 rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 flex items-center"
                onClick={() => setShowParametersModal(true)}
              >
                <img
                  src="/assets/icons/Miljo.png"
                  alt={t("Parametrar")}
                  className="w-5 h-5 mr-2"
                />
                {t('Parametrar')}
              </button>
              <button
                onClick={() => setAdding(!adding)}
                className={
                  adding
                    ? "w-full justify-center bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-6 rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 flex items-center"
                    : "w-full justify-center bg-gradient-to-r from-secondary-green to-secondary-green/90 hover:from-secondary-green/90 hover:to-secondary-green text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] flex items-center"
                }
              >
                {adding ? (
                  <>
                    <img
                      src="/assets/icons/Dolj.png"
                      alt={t("Avbryt")}
                      className="w-5 h-5 mr-2"
                    />
                    {t("Avbryt")}
                  </>
                ) : (
                  <>
                    <img
                      src="/assets/icons/Plus.png"
                      alt={t("Lägg till")}
                      className="w-5 h-5 mr-2"
                    />
                    {t("Lägg till Ingrediens")}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Filter Panel removed */}

          {/* Ingredients Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t("Komponent")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t("Miljövärderingar")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t("Mängd (g/ml)")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t("Procent")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Pris
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t("Ursprung")}
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t("Åtgärder")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredComponents.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-16 text-center">
                      <div className="text-center">
                        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                          <img
                            src="/assets/icons/Innehall.png"
                            alt={t("Inga ingredienser")}
                            className="h-12 w-12 text-gray-400"
                          />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          {t("Inga komponenter hittades")}
                        </h3>
                        <p className="text-gray-600 mb-8">
                          {t("Inga komponenter läggs till ännu.")}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  (() => {
                    const groups = new Map();
                    filteredComponents.forEach((c) => {
                      const key = (c.heading || "").trim();
                      if (!groups.has(key)) groups.set(key, []);
                      groups.get(key).push(c);
                    });
                    const entries = Array.from(groups.entries());
                    return entries.map(([heading, comps], gi) => (
                      <React.Fragment key={`group-${gi}`}>
                        {heading && (
                          <tr key={`group-head-${gi}`}>
                            <td
                              colSpan="7"
                              className="px-6 py-2 bg-gray-50 text-gray-700 font-semibold border-t border-gray-100"
                            >
                              {heading}
                            </td>
                          </tr>
                        )}
                        {comps.map((component) => (
                          <MealTableRowComponent
                            component={component}
                            mealId={mealId}
                            updateItem={() => mealId && dispatch(fetchMealByIdThunk(mealId))}
                            key={component.id}
                            draggable
                            onDragStart={handleDragStart}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                          />
                        ))}
                      </React.Fragment>
                    ));
                  })()
                )}
              </tbody>
            </table>
          </div>


          {/* Add Component Form */}
          <MealTableRowComponentNew
            mealId={mealId}
            adding={adding}
            updateItem={() => mealId && dispatch(fetchMealByIdThunk(mealId))}
          />
          {originalContentsDisplay && (
            <div className="px-6 pb-4 text-sm text-gray-700 m-6 border-t border-gray-100 pt-6">
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <h4 className="text-base font-bold text-gray-900">
                    {t("Ingrediensförteckning")}
                  </h4>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      className="inline-flex items-center bg-white hover:bg-gray-50 text-gray-700 font-semibold text-xs py-2 px-3 rounded-lg border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-1"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(
                            originalContentsDisplay
                          );
                          setCopiedIngredients(true);
                          setTimeout(() => setCopiedIngredients(false), 2000);
                        } catch (e) {
                          console.error(
                            t("Kunde inte kopiera ingredienslista"),
                            e
                          );
                        }
                      }}
                      aria-label={t("Kopiera ingredienslista")}
                    >
                      <img
                        src="/assets/icons/Check.png"
                        alt={t("Kopiera")}
                        className="w-4 h-4 mr-2"
                      />
                      {copiedIngredients ? t("Kopierad!") : t("Kopiera")}
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center bg-white hover:bg-gray-50 text-gray-700 font-semibold text-xs py-2 px-3 rounded-lg border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-1"
                      onClick={() => setShowIngredientsExpanded((v) => !v)}
                      aria-expanded={showIngredientsExpanded}
                      aria-controls="ingredients-original-contents"
                    >
                      <svg
                        className={`w-4 h-4 mr-2 transform transition-transform ${
                          showIngredientsExpanded ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                      {showIngredientsExpanded ? t("Visa mindre") : t("Visa mer")}
                    </button>
                  </div>
                </div>
                <div className="px-5 py-4">
                  <div
                    className={`relative ${
                      showIngredientsExpanded ? "" : "max-h-40 overflow-hidden"
                    }`}
                    id="ingredients-original-contents"
                  >
                    <p className="whitespace-pre-wrap leading-relaxed text-gray-800">
                      {originalContentsDisplay}
                    </p>
                    {!showIngredientsExpanded && (
                      <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-white/0 pointer-events-none" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Description Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mt-8">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h4 className="text-base font-bold text-gray-900">
              {t("Beskrivning av måltiden")}
            </h4>
          </div>
          <div className="px-6 py-4">
            {/* Formatting Help */}
            <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  {t("Formateringstips:")}
                </span>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setDescription(description + "\n• ")}
                    className="text-xs px-2 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 text-gray-600"
                  >
                    {t("Lägg till punkt")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDescription(description + "\n- ")}
                    className="text-xs px-2 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 text-gray-600"
                  >
                    {t("Lägg till bindestreck")}
                  </button>
                </div>
              </div>
              <div className="text-xs text-gray-500 space-y-1">
                <div>• {t("Tryck Enter för ny rad")}</div>
                <div>
                  • {t("Om raden börjar med • eller -, läggs automatiskt till på nästa rad")}
                </div>
                <div>• {t("Använd mellanslag för att skapa struktur")}</div>
              </div>
            </div>

            <textarea
              placeholder={t("Lägg till en beskrivning av måltiden...") + "\n\n" + t("Exempel:") + "\n• " + t("Färsk och näringsrik måltid") + "\n• " + t("Perfekt för frukost") + "\n• " + t("Innehåller lokala ingredienser")}
              maxLength={1000}
              className="w-full h-32 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary-green focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white resize-none font-mono text-xs"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => {
                // Auto-add bullet points on new lines
                if (e.key === "Enter") {
                  e.preventDefault();
                  const cursorPos = e.target.selectionStart;
                  const textBefore = description.substring(0, cursorPos);
                  const textAfter = description.substring(cursorPos);

                  // Check if we're starting a new line
                  const lines = textBefore.split("\n");
                  const currentLine = lines[lines.length - 1];

                  // If current line starts with bullet, add bullet to new line
                  if (
                    currentLine.trim().startsWith("•") ||
                    currentLine.trim().startsWith("-")
                  ) {
                    const newText = textBefore + "\n• " + textAfter;
                    // Check if adding this would exceed 200 characters
                    if (newText.length <= 1000) {
                      setDescription(newText);
                      // Set cursor position after the bullet
                      setTimeout(() => {
                        e.target.setSelectionRange(
                          cursorPos + 3,
                          cursorPos + 3
                        );
                      }, 0);
                    }
                  } else {
                    // Regular new line
                    const newText = textBefore + "\n" + textAfter;
                    // Check if adding this would exceed 200 characters
                    if (newText.length <= 1000) {
                      setDescription(newText);
                      setTimeout(() => {
                        e.target.setSelectionRange(
                          cursorPos + 1,
                          cursorPos + 1
                        );
                      }, 0);
                    }
                  }
                }
              }}
            />
            <div className="mt-2 text-right">
              <span
                className={`text-xs ${
                  description.length > 950
                    ? "text-orange-500"
                    : description.length > 1000
                    ? "text-red-500"
                    : "text-gray-500"
                }`}
              >
                {description.length}/1000 {t("tecken")}
              </span>
            </div>
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={handleSaveDescription}
                disabled={savingDescription}
                className="inline-flex items-center bg-secondary-green hover:bg-secondary-green/90 disabled:bg-gray-400 text-white font-semibold text-xs py-2 px-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-1"
              >
                {savingDescription ? (
                  <>
                    <div className="animate-spin -ml-1 mr-2 h-4 w-4 text-white">
                      <img
                        src="/assets/icons/Innehall.png"
                        alt={t("Laddar")}
                        className="w-full h-full"
                      />
                    </div>
                    {t("Sparar...")}
                  </>
                ) : (
                  <>
                    <img
                      src="/assets/icons/Check.png"
                      alt={t("Spara")}
                      className="w-4 h-4 mr-2"
                    />
                    {t("Spara beskrivning")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Drawer: Nutrients */}
        <div
          className={`fixed inset-0 z-[1250] ${
            showNutrientDrawer ? "pointer-events-auto" : "pointer-events-none"
          }`}
          aria-hidden={!showNutrientDrawer}
        >
          {/* Backdrop */}
          <div
            className={`absolute inset-0 bg-black/30 transition-opacity duration-300 ${
              showNutrientDrawer ? "opacity-100" : "opacity-0"
            }`}
            onClick={() => setShowNutrientDrawer(false)}
          />
          {/* Panel */}
          <div
            className={`absolute right-0 top-0 h-full max-h-screen w-full mobile:w-full tablet:w-[28rem] laptop:w-[32rem] desktop:w-[36rem] bg-white border-l border-gray-200 transform transition-transform duration-300 ${
              showNutrientDrawer ? "translate-x-0" : "translate-x-full"
            } flex flex-col min-h-0 overflow-hidden`}
            role="dialog"
            aria-label={t("Näringsvärde")}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">
                {t("Näringsvärde (per 100g)")}
              </h3>
              <button
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg hover:bg-gray-100 focus:outline-none"
                onClick={() => setShowNutrientDrawer(false)}
              >
                <img
                  src="/assets/icons/Dolj.png"
                  alt={t("Stäng")}
                  className="w-5 h-5 text-gray-600"
                />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-4 pb-24">
              {item && Array.isArray(item.nutrients) && item.nutrients.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          {t("Nutrient")}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          {t("Per 100g")}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          {t("Måltidets")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {(() => {
                        const priority = [
                          "energi",
                          "protein",
                          "fibrer",
                          "fett",
                          "mättat fett",
                          "sockerarter",
                          "kolhydrater",
                          "salt",
                          "kalium",
                          "natrium",
                          "fosfor",
                        ];

                        // Calculate energy from final macro values using multiplying factors
                        const calculatedEnergyPer100g = calculateEnergyFromMacros(item.nutrients, false);

                        // CURRENT: Filter to only show nutrients in the priority list
                        const filtered = item.nutrients.filter((n) => {
                          const nm = (n.name || "")
                            .toString()
                            .trim()
                            .toLowerCase();
                          return priority.includes(nm);
                        });
                        // Sort by priority order
                        const sorted = filtered.sort((a, b) => {
                          const an = (a.name || "")
                            .toString()
                            .trim()
                            .toLowerCase();
                          const bn = (b.name || "")
                            .toString()
                            .trim()
                            .toLowerCase();
                          return priority.indexOf(an) - priority.indexOf(bn);
                        });

                        // ORIGINAL LOGIC (commented out - shows all nutrients):
                        // const byPriority = (n) => {
                        //   const nm = (n.name || "")
                        //     .toString()
                        //     .trim()
                        //     .toLowerCase();
                        //   const idx = priority.indexOf(nm);
                        //   return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
                        // };
                        // const sorted = [...item.nutrients].sort((a, b) => {
                        //   const pa = byPriority(a);
                        //   const pb = byPriority(b);
                        //   if (pa !== pb) return pa - pb;
                        //   const an = (a.name || "").toString().toLowerCase();
                        //   const bn = (b.name || "").toString().toLowerCase();
                        //   if (an < bn) return -1;
                        //   if (an > bn) return 1;
                        //   return 0;
                        // });

                        return sorted.map((n) => {
                          const nutrientName = (n.name || "").toString().trim().toLowerCase();
                          const isEnergy = nutrientName === "energi" || nutrientName === "energy" || nutrientName === "kilokalorier" || nutrientName === "kcal";
                          
                          const rawPer100g =
                            isEnergy && calculatedEnergyPer100g > 0
                              ? calculatedEnergyPer100g
                              : Number(n.amount_per_100g || 0);
                          const mealSize = Number(item?.size || 0);
                          // Ceil for energi to match public product table (kcal as integers); total from raw kcal then ceil
                          const rawMealKcal =
                            mealSize > 0 ? (rawPer100g / 100) * mealSize : 0;
                          const displayAmountPer100g = isEnergy
                            ? Math.ceil(rawPer100g)
                            : rawPer100g;
                          const displayAmountTotal = (() => {
                            if (mealSize <= 0) return 0;
                            if (isEnergy) return Math.ceil(rawMealKcal);
                            return (rawPer100g / 100) * mealSize;
                          })();

                          return (
                            <tr key={`${n.nutrient_id}-${n.unit_id}`}>
                              <td className="px-4 py-2 text-sm text-gray-700">
                                {t(n.name) || n.nutrient_id}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900 text-right">
                                {isEnergy
                                  ? displayAmountPer100g
                                  : displayAmountPer100g.toFixed(2)}{" "}
                                {n.unit_abbr || ""}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900 text-right">
                                {isEnergy
                                  ? displayAmountTotal
                                  : displayAmountTotal.toFixed(2)}{" "}
                                {n.unit_abbr || ""}
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                    </table>
                  </div>
                  <DishEnergyDistributionChart nutrients={item.nutrients} t={t} />
                </>
              ) : (
                <div className="text-sm text-gray-600">
                  {t("Ingen näringsinformation tillgänglig.")}
                </div>
              )}
            </div>
          </div>
        </div>

        <SideDrawer
          open={showCompositionsDrawer}
          onClose={() => setShowCompositionsDrawer(false)}
          title={t("Hälsofaktorer")}
          titleHint={t("Fullkorn, tillsatt socker, grön andel och rött kött")}
          titleIcon={<PiBowlFoodLight className="w-5 h-5" />}
          ariaLabel={t("Hälsofaktorer")}
        >
          <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
            <CompositionPercentagesCard
              values={item?.additional_compositions}
              portionSize={item?.portion_size}
              t={t}
            />
          </div>
        </SideDrawer>

        {/* Modal: Parameters Groups */}
        {showParametersModal && item && (
          <div className="fixed inset-0 z-50 flex items-center justify-center max-h-screen">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-modern opacity-100 transition-opacity"
              onClick={() => setShowParametersModal(false)}
            />
            {/* Modal content */}
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-5xl mx-4 max-h-[90vh] overflow-hidden animate-[fadeIn_0.2s_ease-out]">
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h3 className="text-xl font-bold text-gray-900">
                  {item?.name}
                </h3>
                <button
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 focus:outline-none"
                  onClick={() => setShowParametersModal(false)}
                  aria-label={t("Stäng")}
                >
                  <img
                    src="/assets/icons/svg/Stang.svg"
                    alt={t("Stäng")}
                    className="w-6 h-6"
                  />
                </button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[calc(90vh-64px)] bg-gray-100">
                <ParametersGroups dish={item} t={t} />
              </div>
            </div>
          </div>
        )}
      </div>

      {orderDirty &&
        (typeof document !== "undefined" ? (
          createPortal(
            <div className="fixed inset-x-0 bottom-0 z-50 px-4 py-3 mobile:px-4 tablet:px-6 laptop:px-8 desktop:px-8">
              <div className="max-w-7xl mx-auto">
                <div className="bg-primary-dark border border-gray-200 shadow-lg rounded-xl p-3 flex items-center justify-between text-white">
                  <span className="text-sm">{t("Ordningen har ändrats.")}</span>
                  <div className="flex items-center space-x-2">
                    <button
                      className="bg-gradient-to-r from-secondary-green to-secondary-green/90 hover:from-secondary-green/90 hover:to-secondary-green text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 flex items-center disabled:opacity-50"
                      onClick={handleSaveOrder}
                      disabled={savingOrder}
                    >
                      {savingOrder ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2">
                          <img
                            src="/assets/icons/Innehall.png"
                            alt={t("Laddar")}
                            className="w-full h-full"
                          />
                        </div>
                      ) : (
                        <img
                          src="/assets/icons/Check.png"
                          alt={t("Spara")}
                          className="w-4 h-4 mr-2"
                        />
                      )}
                      {t("Spara ändringar")}
                    </button>
                    <button
                      className="bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 px-4 rounded-lg border border-gray-200 transition-all duration-200"
                      onClick={() => {
                        setOrderDirty(false);
                        mealId && dispatch(fetchMealByIdThunk(mealId));
                      }}
                      disabled={savingOrder}
                    >
                      {t("Avbryt")}
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        ) : (
          <div className="fixed inset-x-0 bottom-0 z-50 px-4 py-3 mobile:px-4 tablet:px-6 laptop:px-8 desktop:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="bg-primary-dark border border-gray-200 shadow-lg rounded-xl p-3 flex items-center justify-between text-white">
                <span className="text-sm">{t("Ordningen har ändrats.")}</span>
                <div className="flex items-center space-x-2">
                  <button
                    className="bg-gradient-to-r from-secondary-green to-secondary-green/90 hover:from-secondary-green/90 hover:to-secondary-green text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 flex items-center disabled:opacity-50"
                    onClick={handleSaveOrder}
                    disabled={savingOrder}
                  >
                    {savingOrder ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2">
                        <img
                          src="/assets/icons/Innehall.png"
                          alt={t("Laddar")}
                          className="w-full h-full"
                        />
                      </div>
                    ) : (
                      <img
                        src="/assets/icons/Check.png"
                        alt={t("Spara")}
                        className="w-4 h-4 mr-2"
                      />
                    )}
                    {t("Spara ändringar")}
                  </button>
                  <button
                    className="bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 px-4 rounded-lg border border-gray-200 transition-all duration-200"
                    onClick={() => {
                      setOrderDirty(false);
                        mealId && dispatch(fetchMealByIdThunk(mealId));
                    }}
                    disabled={savingOrder}
                  >
                    {t("Avbryt")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => !deleting && setShowDeleteModal(false)} />
          <div className="absolute inset-0 flex items-start justify-center p-4 pt-8 overflow-y-auto">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden mt-8">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">
                  {t('Ta bort måltid')}
                </h3>
                <button
                  className="inline-flex items-center justify-center w-9 h-9 rounded-lg hover:bg-gray-100"
                  onClick={() => !deleting && setShowDeleteModal(false)}
                  disabled={deleting}
                >
                  <img src="/assets/icons/Dolj.png" alt={t("Stäng")} className="w-5 h-5" />
                </button>
              </div>
              <div className="px-5 pt-4 pb-2">
                <p className="text-sm text-gray-700">
                  {t('Är du säker på att du vill ta bort måltiden')} "{item?.name}"?
                  {item?.patient_profile_id && (
                    <span className="block mt-2 text-orange-600">
                      {t('Denna måltid är kopplad till en patient.')}
                    </span>
                  )}
                </p>
              </div>
              <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end space-x-3">
                <button
                  className="bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 px-4 rounded-lg border border-gray-200 transition-all duration-200"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                >
                  {t('Avbryt')}
                </button>
                <button
                  className="bg-gradient-to-r from-red-600 to-red-600/90 hover:from-red-600/90 hover:to-red-600 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 disabled:opacity-50"
                  onClick={async () => {
                    setDeleting(true);
                    try {
                      await deleteMeal(mealId);
                      navigate(item?.patient_profile_id ? `/kostdata/patients/${item.patient_profile_id}` : '/');
                    } catch (error) {
                      showAlert(t("Fel"), t('Kunde inte ta bort måltid: ') + error.message);
                    } finally {
                      setDeleting(false);
                      setShowDeleteModal(false);
                    }
                  }}
                  disabled={deleting}
                >
                  {deleting ? t('Tar bort...') : t('Ta bort')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Patient Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => !assigningPatient && setShowAssignModal(false)} />
          <div className="absolute inset-0 flex items-start justify-center p-4 pt-8 overflow-y-auto">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden mt-8">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">
                  {t('Tilldela till patient')}
                </h3>
                <button
                  className="inline-flex items-center justify-center w-9 h-9 rounded-lg hover:bg-gray-100"
                  onClick={() => !assigningPatient && setShowAssignModal(false)}
                  disabled={assigningPatient}
                >
                  <img src="/assets/icons/Dolj.png" alt={t("Stäng")} className="w-5 h-5" />
                </button>
              </div>
              <div className="px-5 pt-4 pb-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Välj patient')}
                </label>
                {patients.length === 0 ? (
                  <div className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-500">
                    {t('Laddar patienter...') || 'Laddar patienter...'}
                  </div>
                ) : (
                  <>
                    <select
                      value={assignPatientId || ''}
                      onChange={(e) => {
                        const selectedId = e.target.value || null;
                        if (selectedId) {
                          const selectedPatient = patients.find(p => p.id.toString() === selectedId);
                          if (selectedPatient && selectedPatient.status === 'inactive') {
                            showAlert(
                              t("Kan inte tilldela"),
                              t('Kan inte tilldela måltid till inaktiv patient. Aktivera patienten först.') || 'Kan inte tilldela måltid till inaktiv patient. Aktivera patienten först.'
                            );
                            setAssignPatientId(null);
                            return;
                          }
                        }
                        setAssignPatientId(selectedId);
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary-green focus:border-transparent"
                    >
                      <option value="">{t('Välj en patient...') || 'Välj en patient...'}</option>
                      {patients.filter(p => p.status === 'active').map((patient) => (
                        <option key={patient.id} value={patient.id}>
                          {patient.name}
                        </option>
                      ))}
                    </select>
                    {patients.filter(p => p.status === 'active').length === 0 && patients.length > 0 && (
                      <p className="mt-2 text-sm text-orange-600">
                        {t('Inga aktiva patienter tillgängliga') || 'Inga aktiva patienter tillgängliga'}
                      </p>
                    )}
                    {patients.filter(p => p.status === 'inactive').length > 0 && (
                      <p className="mt-1 text-xs text-gray-500">
                        {patients.filter(p => p.status === 'inactive').length} {t('inaktiva patienter döljs') || 'inaktiva patienter döljs'}
                      </p>
                    )}
                  </>
                )}
              </div>
              <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end space-x-3">
                <button
                  className="bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 px-4 rounded-lg border border-gray-200 transition-all duration-200"
                  onClick={() => setShowAssignModal(false)}
                  disabled={assigningPatient}
                >
                  {t('Avbryt')}
                </button>
                <button
                  className="bg-gradient-to-r from-secondary-green to-secondary-green/90 hover:from-secondary-green/90 hover:to-secondary-green text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 disabled:opacity-50"
                  onClick={async () => {
                    if (!assignPatientId) {
                      showAlert(t("Valideringsfel"), t('Välj en patient först.'));
                      return;
                    }
                    setAssigningPatient(true);
                    try {
                      await updateMeal(mealId, { patient_profile_id: assignPatientId });
                      dispatch(fetchMealByIdThunk(mealId));
                      setShowAssignModal(false);
                      setAssignPatientId(null);
                    } catch (error) {
                      showAlert(t("Fel"), t('Kunde inte tilldela patient: ') + error.message);
                    } finally {
                      setAssigningPatient(false);
                    }
                  }}
                  disabled={assigningPatient || !assignPatientId}
                >
                  {assigningPatient ? t('Tilldelar...') : t('Tilldela')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {dialogNode}
    </div>
  );
};

export default MealCreationPage;
